import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGame } from '../context/GameContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../api';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHome, FiLogOut } = FiIcons;

const avatarEmojis = {
  lion: '🦁', tiger: '🐯', elephant: '🐘', monkey: '🐵', panda: '🐼',
  koala: '🐨', horse: '🐴', cow: '🐄', pig: '🐷', sheep: '🐑',
  giraffe: '🦒', zebra: '🦓', rhino: '🦏', bear: '🐻', fox: '🦊',
  wolf: '🐺', deer: '🦌', raccoon: '🦝', gorilla: '🦍', penguin: '🐧',
  unicorn: '🦄', dragon: '🐉',
};

const hats = { 'hat-cap': '🧢', 'hat-crown': '👑', 'hat-wizard': '🎩', 'hat-party': '🎉', 'hat-cowboy': '🤠' };
const accessories = { 'acc-glasses': '🤓', 'acc-sunglasses': '😎', 'acc-bowtie': '🎀', 'acc-medal': '🏅' };

function Avatar() {
  const { state, dispatch } = useGame();
  const location = useLocation();
  const navigate = useNavigate();
  const [showCelebration, setShowCelebration] = useState(false);

  const isGameScreen = location.pathname.includes('/game/');
  const isWelcomeScreen = location.pathname === '/';
  const isAuthScreen = ['/login', '/signup'].includes(location.pathname);

  if (!isGameScreen || !state.playerName || isWelcomeScreen || isAuthScreen) return null;

  const handleLogout = () => {
    logout();
    dispatch({ type: 'LOGOUT' });
    navigate('/');
  };

  return (
    <div className="fixed top-4 left-4 z-50 flex items-start gap-2">
      <div className="flex flex-col items-center">
        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
          onClick={() => setShowCelebration(true)}
          className="relative cursor-pointer">
          <div className="bg-white rounded-full p-2 shadow-xl border-4 border-safari-300 w-16 h-16 flex items-center justify-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-jungle-100 to-safari-100 rounded-full" />
            <div className="relative text-3xl z-10">{avatarEmojis[state.avatar?.character] || '🦁'}</div>
            {state.avatar?.hat && hats[state.avatar.hat] && (
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 text-xl z-20">{hats[state.avatar.hat]}</div>
            )}
            {state.avatar?.accessory && accessories[state.avatar.accessory] && (
              <div className="absolute bottom-0 right-0 text-sm z-20">{accessories[state.avatar.accessory]}</div>
            )}
          </div>
          <AnimatePresence>
            {showCelebration && (
              <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }} onAnimationComplete={() => setTimeout(() => setShowCelebration(false), 800)}
                className="absolute -top-5 left-1/2 -translate-x-1/2">
                <div className="bg-yellow-400 text-white px-2 py-0.5 rounded-full font-kid text-xs whitespace-nowrap shadow">✨ Go!</div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
        <div className="mt-1 bg-white/90 px-2 py-0.5 rounded-xl border border-safari-200 shadow text-center min-w-[50px]">
          <span className="text-xs font-comic text-jungle-700 block">Score</span>
          <span className="text-sm font-kid text-safari-600">{state.score}</span>
        </div>
      </div>
      <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
        className="flex flex-col gap-1.5 mt-1">
        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
          onClick={() => navigate('/dashboard')}
          aria-label="Home"
          className="bg-white p-2 rounded-xl shadow border-2 border-safari-200 text-jungle-600 hover:bg-safari-50 transition-colors">
          <SafeIcon icon={FiHome} className="text-lg" />
        </motion.button>
        <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
          onClick={handleLogout}
          aria-label="Logout"
          className="bg-white p-2 rounded-xl shadow border-2 border-red-200 text-red-500 hover:bg-red-50 transition-colors">
          <SafeIcon icon={FiLogOut} className="text-lg" />
        </motion.button>
      </motion.div>
    </div>
  );
}

export default Avatar;